// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
}                                       from 'react';
import { ReactSVG }                     from 'react-svg';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    USER_ACTION_TYPE,
}                                       from '../../enums';

// ===== Services =====

import {
    playAudio,
    recordUserAction,
    getTouchMouseEventHandlers,
    getPressEventHandlers,
    isTestMode,
}                                       from '../../services';
import useLazyAudioRef                           from '../../services/useLazyAudioRef';

// ===== Images =====

import PixelAsteriskIcon                from '../../images/8-bit_asterisk.svg';

// ===== Sounds =====

import ButtonClick                      from '../../sounds/button_click.mp3';

// ===== Constants =====

import {
    DEFAULT_AUDIO_VOLUME,
    HOVER_TARGET_CLASSNAME,
    UNASSIGNED_ERROR_MESSAGE,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';
import KEYCODE                          from '../../constants/keycodes';

// ===== Styles =====

import {
    Container,
    InputHeading,
    Input,
    InputContainer,
    RequiredIndicatorContainer,
}                                       from './styles';

PixelInput.defaultProps = {
    isValid: true,
    isRequired: false,
    onEnter: undefined,
    isEmailField: false,
    inputType: undefined,
    autoComplete: undefined,
    borderColor: undefined,
    focusBorderColor: undefined,
    invalidBorderColor: undefined,
    textColor: undefined,
    focusTextColor: undefined,
    invalidTextColor: undefined,
    placeholderColor: undefined,
    focusPlaceholderColor: undefined,
    invalidPlaceholderColor: undefined,
    headingColor: undefined,
    invalidHeadingColor: undefined,
    inputBackgroundColor: undefined,
};
interface Props {
    customCursorActive: boolean,
    currentSessionId: string | null,
    isValid?: boolean,
    isRequired?: boolean,
    isEmailField?: boolean,
    inputType?: 'text' | 'email' | 'password',
    autoComplete?: string,
    borderColor?: string,
    focusBorderColor?: string,
    invalidBorderColor?: string,
    textColor?: string,
    focusTextColor?: string,
    invalidTextColor?: string,
    placeholderColor?: string,
    focusPlaceholderColor?: string,
    invalidPlaceholderColor?: string,
    headingColor?: string,
    invalidHeadingColor?: string,
    inputBackgroundColor?: string,
    value: string,
    placeholderText: string,
    hasSound: boolean,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    onChange: (value: string) => void,
    onEnter?: () => void,
}
function PixelInput({
    customCursorActive,
    currentSessionId,
    isValid,
    isRequired,
    isEmailField,
    inputType,
    autoComplete,
    borderColor,
    focusBorderColor,
    invalidBorderColor,
    textColor,
    focusTextColor,
    invalidTextColor,
    placeholderColor,
    focusPlaceholderColor,
    invalidPlaceholderColor,
    headingColor,
    invalidHeadingColor,
    inputBackgroundColor,
    value,
    placeholderText,
    hasSound,
    onCursorEnter,
    onCursorLeave,
    setInputFocused,
    onChange,
    onEnter,
}: Props): JSX.Element {
    // ===== Refs =====

    const inputRef = useRef<HTMLInputElement>(null);

    // ----- Sound Clips

    const buttonClickClip = useLazyAudioRef({
        src: ButtonClick,
        volume: DEFAULT_AUDIO_VOLUME,
    });

    // ===== State =====

    const [isFocused, setIsFocused] = useState<boolean>(false);
    const resolvedInputType = inputType || (isEmailField ? 'email' : 'text');

    // ===== Methods =====

    const onInputEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onInputLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(e.target.value);
    };

    /**
     * Manages tracking enter key presses during changes to input
     * @param e keyboard event
     */
    const checkForEnter = (e: React.KeyboardEvent): void => {
        if (e.key === KEYCODE.enter && onEnter) {
            // Cancel the default action, if needed
            e.preventDefault();

            // Respond to enter
            onEnter();
        } else if (e.key === KEYCODE.escape) {
            // Cancel the default action, if needed
            e.preventDefault();

            // Note that ref.current may be null. This is expected, because you may
            // conditionally render the ref-ed element, or you may forgot to assign it
            if (!inputRef.current) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: UNASSIGNED_ERROR_MESSAGE(`${placeholderText} Pixel Input Ref`),
                        },
                    });
                }
                throw Error(UNASSIGNED_ERROR_MESSAGE(`${placeholderText} Pixel Input Ref`));
            }

            // Blur Input
            inputRef.current.blur();
        }
    };

    const focusInput = (): void => {
        if (inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const onInputFocus = (): void => {
        // Inform parent elements
        setInputFocused(true);
        // Change internal state
        setIsFocused(true);

        // Play Sound
        if (hasSound && buttonClickClip.current) {
            buttonClickClip.current.pause();
            buttonClickClip.current.currentTime = 0;
            playAudio(buttonClickClip.current);
        }
    };

    const onInputBlur = (): void => {
        // Inform parent elements
        setInputFocused(false);
        // Change internal state
        setIsFocused(false);
    };

    // ===== Side Effects =====

    useEffect(() => {
        if (
            value === ''
            && inputRef.current
            && inputRef.current.value.length > 0
        ) {
            inputRef.current.value = '';
        }
    }, [value]);

    return (
        <Container
            isInvalid={!isValid}
            isFocused={isFocused}
            borderColor={borderColor}
            focusBorderColor={focusBorderColor}
            invalidBorderColor={invalidBorderColor}
            className={HOVER_TARGET_CLASSNAME}
            {...getTouchMouseEventHandlers({
                onMouseEnter: (e) => onInputEnter(e),
                onMouseLeave: (e) => onInputLeave(e),
            })}
            {...getPressEventHandlers({ onMouseDown: focusInput })}
        >
            {value.length > 0 && (
                <InputHeading
                    isInvalid={!isValid}
                    headingColor={headingColor}
                    invalidHeadingColor={invalidHeadingColor}
                >
                    {placeholderText}
                </InputHeading>
            )}
            <InputContainer
                noText={value.length === 0}
                inputBackgroundColor={inputBackgroundColor}
            >
                <Input
                    type={resolvedInputType}
                    inputMode={resolvedInputType === 'email' ? 'email' : undefined}
                    autoCapitalize={resolvedInputType === 'email' ? 'none' : undefined}
                    autoCorrect={resolvedInputType === 'email' ? 'off' : undefined}
                    spellCheck={resolvedInputType === 'email' ? false : undefined}
                    autoComplete={autoComplete || (resolvedInputType === 'email' ? 'email' : undefined)}
                    ref={inputRef}
                    customCursorActive={customCursorActive}
                    defaultValue={value}
                    placeholder={placeholderText}
                    onChange={onInputChange}
                    onKeyUp={checkForEnter}
                    isInvalid={!isValid}
                    textColor={textColor}
                    focusTextColor={focusTextColor}
                    invalidTextColor={invalidTextColor}
                    placeholderColor={placeholderColor}
                    focusPlaceholderColor={focusPlaceholderColor}
                    invalidPlaceholderColor={invalidPlaceholderColor}
                    disabled={false}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}
                />
                {isRequired && (
                    <RequiredIndicatorContainer
                        isInvalid={!isValid}
                    >
                        <ReactSVG
                            src={PixelAsteriskIcon}
                        />
                    </RequiredIndicatorContainer>
                )}
            </InputContainer>
        </Container>
    );
}

export default PixelInput;
